import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'
import { PayoutCompany } from '@/types'

export interface PayoutCompanyFormData {
  name: string
  organizationNumber: string
  address: string
  city: string
  postalCode: string
  phoneNumber: string
}

interface Props {
  initial?: Partial<PayoutCompanyFormData>
  onSubmit: (data: PayoutCompanyFormData) => Promise<void>
  onCancel: () => void
  submitLabel?: string
}

export function PayoutCompanyForm({ initial, onSubmit, onCancel, submitLabel = 'Spara' }: Props) {
  const [form, setForm] = useState<PayoutCompanyFormData>({
    name: initial?.name ?? '',
    organizationNumber: initial?.organizationNumber ?? '',
    address: initial?.address ?? '',
    city: initial?.city ?? '',
    postalCode: initial?.postalCode ?? '',
    phoneNumber: initial?.phoneNumber ?? ''
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const set = (field: keyof PayoutCompanyFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }))
    if (error) setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('Bolagsnamn är obligatoriskt')
      return
    }
    setSaving(true)
    try {
      await onSubmit(form)
    } catch (err: any) {
      setError(err.message || 'Något gick fel')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="pc-name">
          Namn <span className="text-red-500">*</span>
        </Label>
        <Input
          id="pc-name"
          value={form.name}
          onChange={set('name')}
          placeholder="Bolagsnamn AB"
          disabled={saving}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="pc-org">Organisationsnummer</Label>
        <Input
          id="pc-org"
          value={form.organizationNumber}
          onChange={set('organizationNumber')}
          placeholder="XXXXXXXX-XXXX"
          disabled={saving}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="pc-address">Adress</Label>
        <Input
          id="pc-address"
          value={form.address}
          onChange={set('address')}
          placeholder="Gatuadress och nummer"
          disabled={saving}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="pc-city">Ort</Label>
          <Input
            id="pc-city"
            value={form.city}
            onChange={set('city')}
            placeholder="Stad"
            disabled={saving}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pc-postal">Postnummer</Label>
          <Input
            id="pc-postal"
            value={form.postalCode}
            onChange={set('postalCode')}
            placeholder="123 45"
            disabled={saving}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="pc-phone">Telefon</Label>
        <Input
          id="pc-phone"
          value={form.phoneNumber}
          onChange={set('phoneNumber')}
          placeholder="08-123 45 67"
          disabled={saving}
        />
      </div>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Avbryt
        </Button>
        <Button type="submit" disabled={saving || !form.name.trim()}>
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}

/** Convert a PayoutCompany to form values */
export function companyToFormData(company: PayoutCompany): PayoutCompanyFormData {
  return {
    name: company.name,
    organizationNumber: company.organizationNumber ?? '',
    address: company.address ?? '',
    city: company.city ?? '',
    postalCode: company.postalCode ?? '',
    phoneNumber: company.phoneNumber ?? ''
  }
}
